/**
 * Internal proposal-level hardware MARK UP.
 *
 * sellingUnitPrice = baseUnitPrice × (1 + markupPercentage / 100)
 * Never overwrite baseUnitPrice with the selling price.
 * Customers see only selling prices — never markup % / base / commission wording.
 */
import type { DtoolsItemBucket } from "./dtoolsSiJsonImport";
import { isClarityStandardServiceProduct } from "./clarityServiceCatalogueItems";
import { isEquipmentPercentagePricingMethod } from "./equipmentPercentagePricing";

export const PROPOSAL_MARKUP_PERCENT_MAX = 100;
export const PROPOSAL_MARKUP_PERCENT_MIN = 0;

export type ProposalHardwareMarkupSettings = {
  markupEnabled: boolean;
  markupPercentage: number;
};

export const DEFAULT_PROPOSAL_HARDWARE_MARKUP: ProposalHardwareMarkupSettings = {
  markupEnabled: false,
  markupPercentage: 0,
};

function roundMoney(n: number): number {
  return Math.round(n * 100) / 100;
}

function moneyNumber(value: unknown): number {
  const n = typeof value === "number" ? value : Number(value ?? 0);
  return Number.isFinite(n) ? n : 0;
}

/** Clamp markup % consistently with other financial inputs (0–100, 4 dp). */
export function normalizeMarkupPercentage(value: unknown): number {
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return 0;
  const clamped = Math.min(PROPOSAL_MARKUP_PERCENT_MAX, Math.max(PROPOSAL_MARKUP_PERCENT_MIN, n));
  return Math.round(clamped * 10000) / 10000;
}

export function readProposalHardwareMarkup(retailPricingJson: unknown): ProposalHardwareMarkupSettings {
  if (!retailPricingJson || typeof retailPricingJson !== "object" || Array.isArray(retailPricingJson)) {
    return { ...DEFAULT_PROPOSAL_HARDWARE_MARKUP };
  }
  const raw = retailPricingJson as Record<string, unknown>;
  const enabled = raw.markupEnabled === true;
  const percentage = normalizeMarkupPercentage(raw.markupPercentage ?? 0);
  return {
    markupEnabled: enabled,
    markupPercentage: enabled ? percentage : 0,
  };
}

export function mergeProposalHardwareMarkup(
  retailPricingJson: Record<string, unknown> | null | undefined,
  patch: Partial<ProposalHardwareMarkupSettings>,
): Record<string, unknown> {
  const base = retailPricingJson && typeof retailPricingJson === "object" ? { ...retailPricingJson } : {};
  const prev = readProposalHardwareMarkup(base);
  const markupEnabled = patch.markupEnabled !== undefined ? Boolean(patch.markupEnabled) : prev.markupEnabled;
  const markupPercentage = normalizeMarkupPercentage(
    patch.markupPercentage !== undefined ? patch.markupPercentage : prev.markupPercentage,
  );
  return {
    ...base,
    markupEnabled,
    markupPercentage: markupEnabled ? markupPercentage : 0,
  };
}

/**
 * Strip internal markup fields before any customer-facing payload.
 * Customers must never see markup %, amounts, or commission/margin wording.
 */
export function stripMarkupFromCustomerRetailPricing<T extends Record<string, unknown> | null | undefined>(
  retailPricingJson: T,
): T {
  if (!retailPricingJson || typeof retailPricingJson !== "object" || Array.isArray(retailPricingJson)) {
    return retailPricingJson;
  }
  const next = { ...(retailPricingJson as Record<string, unknown>) };
  delete next.markupEnabled;
  delete next.markupPercentage;
  delete next.markupAmount;
  delete next.baseUnitPrice;
  delete next.commission;
  delete next.margin;
  return next as T;
}

export function customerRetailPricingHasMarkupLeak(retailPricingJson: unknown): boolean {
  if (!retailPricingJson || typeof retailPricingJson !== "object" || Array.isArray(retailPricingJson)) {
    return false;
  }
  const raw = retailPricingJson as Record<string, unknown>;
  return (
    "markupEnabled" in raw ||
    "markupPercentage" in raw ||
    "markupAmount" in raw ||
    (typeof raw.commission === "number" && raw.commission > 0)
  );
}

/** Remove internal base/markup metadata from a persisted D-Tools line before public serialization. */
export function stripMarkupFromCustomerLineRawText(rawText: unknown): string {
  const text = String(rawText ?? "");
  if (!text.trim().startsWith("{")) return text;
  try {
    const parsed = JSON.parse(text) as Record<string, unknown>;
    delete parsed.baseUnitPrice;
    delete parsed.markupEnabled;
    delete parsed.markupPercentage;
    delete parsed.markupAmount;
    delete parsed.commission;
    delete parsed.margin;
    return JSON.stringify(parsed);
  } catch {
    return text;
  }
}

export function computeSellingUnitPrice(baseUnitPrice: number, markupPercentage: number): number {
  const base = Math.max(0, moneyNumber(baseUnitPrice));
  const pct = normalizeMarkupPercentage(markupPercentage);
  if (pct <= 0) return roundMoney(base);
  return roundMoney(base * (1 + pct / 100));
}

export type MarkupEligibleLineFields = {
  bucket?: DtoolsItemBucket | string | null;
  manufacturer?: string | null;
  brand?: string | null;
  model?: string | null;
  name?: string | null;
  displayName?: string | null;
  sku?: string | null;
  catalogueKey?: string | null;
  pricingMethod?: unknown;
  isAccessory?: boolean | null;
};

/**
 * Hardware products receive markup. Services, discounts, cables, Clarity IC/% lines do not.
 */
export function isHardwareMarkupEligible(line: MarkupEligibleLineFields): boolean {
  const bucket = String(line.bucket ?? "commercial").toLowerCase();
  if (bucket === "service" || bucket === "discount" || bucket === "cable") return false;
  if (bucket === "optional" || bucket === "non_billable") return false;
  if (isEquipmentPercentagePricingMethod(line.pricingMethod)) return false;
  if (
    isClarityStandardServiceProduct({
      brand: line.brand ?? line.manufacturer,
      manufacturer: line.manufacturer,
      model: line.model,
      name: line.name,
      displayName: line.displayName,
      sku: line.sku,
      catalogueKey: line.catalogueKey,
    })
  ) {
    return false;
  }
  return bucket === "commercial";
}

export type MarkupPricedLine = {
  unitPrice: number;
  lineTotal: number;
  quantity: number;
  baseUnitPrice?: number | null;
  bucket?: DtoolsItemBucket | string | null;
  manufacturer?: string | null;
  brand?: string | null;
  model?: string | null;
  name?: string | null;
  displayName?: string | null;
  sku?: string | null;
  catalogueKey?: string | null;
  pricingMethod?: unknown;
};

/**
 * Recalculate selling unitPrice / lineTotal from baseUnitPrice for eligible hardware.
 * When markup is off, restores selling prices to base.
 */
export function applyHardwareMarkupToLines<T extends MarkupPricedLine>(
  lines: T[],
  settings: ProposalHardwareMarkupSettings,
): T[] {
  const enabled = settings.markupEnabled === true;
  const pct = enabled ? normalizeMarkupPercentage(settings.markupPercentage) : 0;

  return lines.map((line) => {
    const qty = Math.max(1, Math.round(moneyNumber(line.quantity) || 1));
    const base =
      line.baseUnitPrice != null && Number.isFinite(Number(line.baseUnitPrice))
        ? Math.max(0, moneyNumber(line.baseUnitPrice))
        : Math.max(0, moneyNumber(line.unitPrice));

    if (!isHardwareMarkupEligible(line)) {
      return {
        ...line,
        baseUnitPrice: line.baseUnitPrice != null ? roundMoney(base) : line.baseUnitPrice,
        quantity: qty,
      };
    }

    const selling = computeSellingUnitPrice(base, pct);
    return {
      ...line,
      baseUnitPrice: roundMoney(base),
      unitPrice: selling,
      quantity: qty,
      lineTotal: roundMoney(selling * qty),
    };
  });
}

/** Resolve base unit price for a line (never the marked-up selling price when base is stored). */
export function resolveLineBaseUnitPrice(line: {
  baseUnitPrice?: number | null;
  unitPrice?: number | null;
}): number {
  if (line.baseUnitPrice != null && Number.isFinite(Number(line.baseUnitPrice))) {
    return roundMoney(Math.max(0, moneyNumber(line.baseUnitPrice)));
  }
  return roundMoney(Math.max(0, moneyNumber(line.unitPrice)));
}

/** Equipment contribution using base prices (pre-markup) when selling differs from base. */
export function equipmentBaseAmountFromLine(line: {
  baseUnitPrice?: number | null;
  unitPrice?: number | null;
  lineTotal?: number | null;
  quantity?: number | null;
}): number {
  const qty = Math.max(1, Math.round(moneyNumber(line.quantity) || 1));
  if (line.baseUnitPrice != null && Number.isFinite(Number(line.baseUnitPrice))) {
    const base = Math.max(0, moneyNumber(line.baseUnitPrice));
    const unit = Math.max(0, moneyNumber(line.unitPrice));
    // Markup applied: selling ≠ base → percentage services use pre-markup base.
    if (Math.abs(base - unit) > 0.005) {
      return roundMoney(base * qty);
    }
  }
  // No markup spread (or legacy rows): prefer authoritative lineTotal.
  if (line.lineTotal != null && Number.isFinite(Number(line.lineTotal))) {
    return roundMoney(Math.max(0, moneyNumber(line.lineTotal)));
  }
  return roundMoney(Math.max(0, moneyNumber(line.unitPrice)) * qty);
}
